import {t} from '@/lib/i18n'
import type {SiteLite} from '@/multisite/types'

export type SpecialPageKey = 'products' | 'blog' | 'dealers' | 'campaigns' | 'manuals' | 'search'

type SpecialPageConfig = {
  fallbackPath: string
  fallbackTitle: string
  defaultIntro: string
}

const specialPageConfig: Record<SpecialPageKey, SpecialPageConfig> = {
  products: {
    fallbackPath: 'products',
    fallbackTitle: 'Products',
    defaultIntro: 'Browse the standard products landing page for this site.',
  },
  blog: {
    fallbackPath: 'blog',
    fallbackTitle: 'Blog',
    defaultIntro: 'Browse the latest articles for this site.',
  },
  dealers: {
    fallbackPath: 'dealers',
    fallbackTitle: 'Dealers',
    defaultIntro: 'Browse the standard dealers landing page for this site.',
  },
  campaigns: {
    fallbackPath: 'campaigns',
    fallbackTitle: 'Campaigns',
    defaultIntro: 'Browse the current campaigns for this site.',
  },
  manuals: {
    fallbackPath: 'manuals',
    fallbackTitle: 'Manuals',
    defaultIntro: 'Browse the standard manuals landing page for this site.',
  },
  search: {
    fallbackPath: 'search',
    fallbackTitle: 'Search',
    defaultIntro: 'Browse sitewide search results for this site.',
  },
}

export const specialPageKeys = Object.keys(specialPageConfig) as SpecialPageKey[]

const trimSlashes = (value?: string | null) => (value || '').replace(/^\/+|\/+$/g, '')

const joinSitePath = (siteBasePath: string, sectionBasePath: string): string => {
  const parts = [trimSlashes(siteBasePath), trimSlashes(sectionBasePath)].filter(Boolean)
  return `/${parts.join('/')}`
}

export const splitPathSegments = (value?: string | null) =>
  trimSlashes(value)
    .split('/')
    .map((segment) => segment.trim())
    .filter(Boolean)

export const isSameSegments = (a: string[], b: string[]) =>
  a.length === b.length && a.every((segment, index) => segment === b[index])

export const specialPageBaseSegment = (site: SiteLite, key: SpecialPageKey): string => {
  const config = specialPageConfig[key]
  const raw = site.basePages?.[key]?.basePath
  return trimSlashes(typeof raw === 'string' ? raw : '') || config.fallbackPath
}

export const specialPagePath = (site: SiteLite, key: SpecialPageKey): string =>
  joinSitePath(site.routing.basePath, specialPageBaseSegment(site, key))

export const specialPageSegments = (site: SiteLite, key: SpecialPageKey): string[] =>
  splitPathSegments(specialPagePath(site, key))

export const specialPageRouteSegments = (site: SiteLite, key: SpecialPageKey): string[] =>
  splitPathSegments(specialPageBaseSegment(site, key))

export const matchSpecialPage = (site: SiteLite, slugSegments: string[]): SpecialPageKey | null => {
  for (const key of specialPageKeys) {
    if (
      isSameSegments(slugSegments, specialPageSegments(site, key)) ||
      isSameSegments(slugSegments, specialPageRouteSegments(site, key))
    ) {
      return key
    }
  }
  return null
}

export const specialPageTitle = (site: SiteLite, key: SpecialPageKey): string => {
  const config = specialPageConfig[key]
  const raw = site.basePages?.[key]?.title
  if (typeof raw === 'string' && raw.trim().length > 0) return raw
  // No title in the CMS: a translated fallback, so a Norwegian site without a
  // search-page title reads "Søk", not "Search" (reported 2026-10-27).
  return t(site.locale, `specialPages.${key}.title`, config.fallbackTitle)
}

export const specialPageBannerImage = (site: SiteLite, key: SpecialPageKey) =>
  site.basePages?.[key]?.bannerImage

export const specialPagePretitle = (site: SiteLite, key: SpecialPageKey) =>
  site.basePages?.[key]?.pretitle

export const specialPageDescription = (site: SiteLite, key: SpecialPageKey) =>
  site.basePages?.[key]?.description

export const specialPageIntro = (key: SpecialPageKey): string => specialPageConfig[key].defaultIntro

// Localized meta-description fallback. Editor-supplied `basePages.<key>.description`
// wins; otherwise a translated default per key so SEO floor (≥0.9) holds even when
// editors leave the field blank. `search` is `noIndex` — no fallback needed.
//
// Maps to existing i18n namespaces (singular `product`/`campaign`, `articles` for
// blog) rather than the SpecialPageKey verbatim, to keep catalog conventions stable.
const metaDescriptionConfig: Record<
  Exclude<SpecialPageKey, 'search'>,
  {key: string; fallback: string}
> = {
  products: {
    key: 'product.metaDescription',
    fallback:
      'Discover the Jøtul range of wood stoves, inserts, fireplaces and accessories. Compare models and find the right fit for your home.',
  },
  blog: {
    key: 'articles.metaDescription',
    fallback:
      'Read the latest stories, guides and inspiration from Jøtul — from buying advice to installation tips and design ideas.',
  },
  dealers: {
    key: 'dealers.metaDescription',
    fallback:
      'Find your nearest authorised Jøtul dealer for in-person advice, demonstrations and after-sales service.',
  },
  campaigns: {
    key: 'campaign.metaDescription',
    fallback: 'Explore current promotions, seasonal offers and dealer events from Jøtul.',
  },
  manuals: {
    key: 'manuals.metaDescription',
    fallback:
      'Download user manuals, installation guides and technical documents for Jøtul products.',
  },
}

export const specialPageMetaDescription = (
  site: SiteLite,
  key: SpecialPageKey,
  locale?: string,
): string | undefined => {
  const override = specialPageDescription(site, key)
  if (override) return override
  if (key === 'search') return undefined
  const {key: messageKey, fallback} = metaDescriptionConfig[key]
  return t(locale, messageKey, fallback)
}
